<template>
	<base-dialog v-model="showModal" size="md" :okFn="() => { updateLastHealthReminder(true) }" :cancelFn="updateLastHealthReminder" keepOpened :okBtnText="$t('save')" :hideFooter="variables.length === 1">
		<b-alert variant="secondary" show class="mb-4" v-if="showTitle">{{ $t('health-sliders-modal-explanation') }}</b-alert>
		<div v-for="(item, propName) in additionalVariables" :key="propName">
			<clinic-history-variable v-if="variables.includes(item.variableName)" :ref="`var-${propName}`" :rawData="item" :name="propName" :value="[5]" :variableName="item.variableName" class="mb-2" editable slider :graphEnabled="false" :label="$t(`health-slider-label.${propName}`)" hideDate verticalMode @save="onVariableSave" :saveOnChange="false" daily :centered="propName == HEALTH_CONDITION_TODAY" :showTrackingButton="false"></clinic-history-variable>
			<div v-show="showTitle && propName == HEALTH_CONDITION_TODAY">
				<hr>
				<p>
					{{ $t('health-sliders-modal-last-7-days') }}
				</p>
			</div>
		</div>
	</base-dialog>
</template>

<script>
import BaseDialog from '@/components/base/BaseDialog'
import { BAlert, ToastPlugin } from 'bootstrap-vue'
import PatientsRepository from '@/repositories/PatientsRepository'
import ClinicHistoryRepository from '@/repositories/ClinicHistoryRepository'
import ClinicHistoryVariable from '@/components/shared/ClinicHistoryVariable.vue'
import { SLEEP_QUALITY, HEALTH_CONDITION, FEEDING_QUALITY, EXERCISE_AMOUNT, STRESS, HEALTH_CONDITION_TODAY } from '@/constants/clinicHistory'
import { uniq } from 'lodash'
import Vue from 'vue'
import { UPDATE_HEALTH_QUESTIONNAIRE_ACTION } from '@/constants/loginOptionalActions'

Vue.use(ToastPlugin)

const SAVE_EVENT = 'save'

const i18n = {
	messages: {
		es: {
			'health-slider-label': {
				[HEALTH_CONDITION_TODAY]: '¿Cómo te sientes hoy?',
				[HEALTH_CONDITION]: '¿Cómo te encuentras de salud?',
				[FEEDING_QUALITY]: '¿Cómo has comido (saludable)?',
				[SLEEP_QUALITY]: '¿Cómo has dormido?',
				[EXERCISE_AMOUNT]: '¿Has hecho ejercicio?',
				[STRESS]: 'Indica tu nivel de estrés'
			},
			'health-sliders-modal-explanation': 'Para mantener actualizados tus datos de salud te pediremos periódicamente que completes un breve cuestionario.\nSolo tú o el profesional que tú elijas podrá acceder a esta información.',
			'health-sliders-modal-last-7-days': 'En los últimos siete días...',
			'health-sliders-modal-not-all-variables-saved': 'Debes guardar todas las variables para poder continuar.'
		},
		en: {
			'health-slider-label': {
				[HEALTH_CONDITION_TODAY]: 'How do you feel today?',
				[HEALTH_CONDITION]: 'How do you feel?',
				[FEEDING_QUALITY]: 'How have you eaten (healthy)?',
				[SLEEP_QUALITY]: 'How have you slept?',
				[EXERCISE_AMOUNT]: 'Have you exercised?',
				[STRESS]: 'Indicate your stress level'
			},
			'health-sliders-modal-explanation': 'To keep your health data updated, we will periodically ask you to complete a brief questionnaire.\nOnly you or the professional you choose will be able to access this information.',
			'health-sliders-modal-last-7-days': 'In the last seven days...',
			'health-sliders-modal-not-all-variables-saved': 'You must save all the variables to continue.'
		},
		pt: {
			'health-slider-label': {
				[HEALTH_CONDITION_TODAY]: 'Como você se sente hoje?',
				[HEALTH_CONDITION]: 'Como você se sente?',
				[FEEDING_QUALITY]: 'Como você comeu (saudável)?',
				[SLEEP_QUALITY]: 'Como você dormiu?',
				[EXERCISE_AMOUNT]: 'Você fez exercício?',
				[STRESS]: 'Indique seu nível de estresse'
			},
			'health-sliders-modal-explanation': 'Para manter seus dados de saúde atualizados, periodicamente pediremos que você complete um breve questionário.\nSomente você ou o profissional que você escolher poderá acessar essas informações.',
			'health-sliders-modal-last-7-days': 'Nos últimos sete dias...',
			'health-sliders-modal-not-all-variables-saved': 'Você deve salvar todas as variáveis para continuar.'
		}
	}
}

export default {
	i18n,
	components: {
		'clinic-history-variable': ClinicHistoryVariable,
		BAlert,
		BaseDialog
	},
	props: {
		variables: {
			type: Array,
			required: false,
			default () {
				return [FEEDING_QUALITY, SLEEP_QUALITY, EXERCISE_AMOUNT, STRESS]
			}
		},
		showTitle: {
			type: Boolean,
			required: false,
			default: true
		}
	},
	data () {
		return {
			additionalVariables: null,
			showModal: true,
			HEALTH_CONDITION_TODAY,
			savedVariables: []
		}
	},
	methods: {
		updateLastHealthReminder (checkSavedVariables = false) {
			if (checkSavedVariables && !this.allVariablesSaved) {
				this.$store.dispatch('showAlert', {
					text: this.$t('health-sliders-modal-not-all-variables-saved'),
					variant: 'info'
				})
				return
			}
			PatientsRepository.updatePatient({ updateLastHealthReminder: true }).then(() => {
				this.showModal = false
				this.$store.commit('session/removeLoginOptionalAction', UPDATE_HEALTH_QUESTIONNAIRE_ACTION)
			})
		},
		onVariableSave (variableName, values) {
			this.savedVariables.push(variableName)
			this.$emit(SAVE_EVENT, variableName, values)
			if (this.variables.length === 1) {
				this.showModal = false
			}
		}
	},
	mounted () {
		ClinicHistoryRepository().getAllVariables().then((response) => {
			const variables = response.data.data.additionalVariables
			// Remove variables that are not in the prop list (variables is an object with multiple keys)
			this.additionalVariables = Object.keys(variables).reduce((acc, key) => {
				if (this.variables.includes(variables[key].variableName)) {
					acc[key] = variables[key]
				}
				return acc
			}, {})
			// Place healthcondition variable in first position in the object (only for 360 users)
			/* if (this.$store.getters['session/userHasCompanies']) {
				const healthCondition = this.additionalVariables[HEALTH_CONDITION_TODAY]
				delete this.additionalVariables[HEALTH_CONDITION_TODAY]
				this.additionalVariables = { [HEALTH_CONDITION_TODAY]: healthCondition, ...this.additionalVariables }
			} */
		})
	},
	computed: {
		allVariablesSaved () {
			return this.additionalVariables && uniq(this.computedSavedVariables).filter((variable) => Object.keys(this.additionalVariables).includes(variable)).length === Object.keys(this.additionalVariables).length
		},
		computedSavedVariables () {
			const varRefs = Object.values(this.$refs).filter((ref) => this.variables.includes(ref[0].name) && ref[0]._computedWatchers.alreadySavedDailyVariable.value)
			return [...this.savedVariables, ...varRefs.map((ref) => ref[0].name)]
		}
	}
}
</script>

<style>

</style>


// WEBPACK FOOTER //
// src/components/webapp/shared/HealthSlidersModal.vue